/**
 * EffectsPresetSelector Component
 * 
 * Dropdown selector for effects chain presets in the EffectsPanel header.
 * Displays presets organized by category with favorites at the top.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { effectsPresetService, type EffectsChainPreset } from '../../services/effects/EffectsPresetService';
import type { EffectInstance } from '../../services/effects/EffectsManager';
import './EffectsPresetSelector.css';

/**
 * Props for EffectsPresetSelector component
 */
export interface EffectsPresetSelectorProps {
  /** Row index */
  row: number;
  /** Current effects chain */
  currentEffects: EffectInstance[]; // eslint-disable-line @typescript-eslint/no-unused-vars
  /** Callback when a preset is selected */
  onPresetSelect: (preset: EffectsChainPreset) => void;
  /** Callback to open save preset dialog */
  onSavePreset: () => void;
  /** Callback to open preset management menu */
  onManagePresets: (preset: EffectsChainPreset) => void;
}

/**
 * EffectsPresetSelector component
 * 
 * Renders a dropdown selector with:
 * - Favorite presets at the top
 * - Presets organized by category
 * - Save current preset option
 * - Manage presets option
 * 
 * @param props - Component props
 * @returns EffectsPresetSelector component
 */
export function EffectsPresetSelector({
  row,
  currentEffects: _currentEffects, // Prefix with underscore to indicate intentionally unused
  onPresetSelect,
  onSavePreset,
  onManagePresets,
}: EffectsPresetSelectorProps) {
  const [presets, setPresets] = useState<EffectsChainPreset[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);

  /**
   * Load presets on mount
   */
  useEffect(() => {
    const loadPresets = () => {
      const allPresets = effectsPresetService.getAllPresets();
      setPresets(allPresets);
    };

    loadPresets();
  }, []);

  /**
   * Organize presets by category with favorites at top
   */
  const organizedPresets = useMemo(() => {
    const favorites = presets.filter(p => p.isFavorite);
    const byCategory: Record<string, EffectsChainPreset[]> = {};

    // Group non-favorite presets by category
    presets.filter(p => !p.isFavorite).forEach(preset => {
      const category = preset.category || 'General';
      if (!byCategory[category]) {
        byCategory[category] = [];
      }
      byCategory[category].push(preset);
    });

    return { favorites, byCategory };
  }, [presets]);

  /**
   * Handle preset selection
   */
  const handlePresetSelect = useCallback((preset: EffectsChainPreset) => {
    setSelectedPresetId(preset.id);
    onPresetSelect(preset);
    setIsOpen(false);
  }, [onPresetSelect]);

  /**
   * Toggle dropdown
   */
  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  /**
   * Close dropdown when clicking outside
   */
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.effects-preset-selector-wrapper')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /**
   * Get display text for selected preset
   */
  const selectedPresetName = useMemo(() => {
    if (!selectedPresetId) return 'No Preset';
    const preset = presets.find(p => p.id === selectedPresetId);
    return preset?.name || 'No Preset';
  }, [selectedPresetId, presets]);

  return (
    <div className="effects-preset-selector-wrapper">
      <button
        className="effects-preset-selector-btn"
        onClick={toggleDropdown}
        aria-label={`Effects preset selector for row ${row + 1}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
        type="button"
      >
        <span className="effects-preset-selector-text">{selectedPresetName}</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
      </button>

      {isOpen && (
        <div className="effects-preset-dropdown" role="menu">
          {/* Save Current Preset */}
          <button
            className="effects-preset-dropdown-item effects-preset-action"
            onClick={() => {
              onSavePreset();
              setIsOpen(false);
            }}
            role="menuitem"
            type="button"
          >
            <i className="fas fa-save" aria-hidden="true"></i>
            <span>Save Current Preset</span>
          </button>

          {/* Clear Selection */}
          {selectedPresetId && (
            <button
              className="effects-preset-dropdown-item effects-preset-action"
              onClick={() => {
                setSelectedPresetId(null);
                setIsOpen(false);
              }}
              role="menuitem"
              type="button"
            >
              <i className="fas fa-times" aria-hidden="true"></i>
              <span>Clear Selection</span>
            </button>
          )}

          <div className="effects-preset-dropdown-divider" role="separator"></div>

          {/* Favorite Presets */}
          {organizedPresets.favorites.length > 0 && (
            <>
              <div className="effects-preset-dropdown-category">
                <i className="fas fa-star" aria-hidden="true"></i>
                <span>Favorites</span>
              </div>
              {organizedPresets.favorites.map(preset => (
                <div key={preset.id} className="effects-preset-dropdown-item-wrapper">
                  <button
                    className={`effects-preset-dropdown-item ${selectedPresetId === preset.id ? 'selected' : ''}`}
                    onClick={() => handlePresetSelect(preset)}
                    role="menuitem"
                    type="button"
                  >
                    <span className="effects-preset-name">{preset.name}</span>
                    {preset.description && (
                      <span className="effects-preset-description">{preset.description}</span>
                    )}
                  </button>
                  <button
                    className="effects-preset-manage-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onManagePresets(preset);
                      setIsOpen(false);
                    }}
                    aria-label={`Manage ${preset.name} preset`}
                    type="button"
                  >
                    <i className="fas fa-ellipsis-v" aria-hidden="true"></i>
                  </button>
                </div>
              ))}
              <div className="effects-preset-dropdown-divider" role="separator"></div>
            </>
          )}

          {/* Presets by Category */}
          {Object.entries(organizedPresets.byCategory).map(([category, categoryPresets]) => (
            <div key={category}>
              <div className="effects-preset-dropdown-category">
                <span>{category}</span>
              </div>
              {categoryPresets.map(preset => (
                <div key={preset.id} className="effects-preset-dropdown-item-wrapper">
                  <button
                    className={`effects-preset-dropdown-item ${selectedPresetId === preset.id ? 'selected' : ''}`}
                    onClick={() => handlePresetSelect(preset)}
                    role="menuitem"
                    type="button"
                  >
                    <span className="effects-preset-name">{preset.name}</span>
                    {preset.description && (
                      <span className="effects-preset-description">{preset.description}</span>
                    )}
                  </button>
                  <button
                    className="effects-preset-manage-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      onManagePresets(preset);
                      setIsOpen(false);
                    }}
                    aria-label={`Manage ${preset.name} preset`}
                    type="button"
                  >
                    <i className="fas fa-ellipsis-v" aria-hidden="true"></i>
                  </button>
                </div>
              ))}
            </div>
          ))}

          {/* Empty State */}
          {presets.length === 0 && (
            <div className="effects-preset-dropdown-empty">
              <i className="fas fa-inbox" aria-hidden="true"></i>
              <span>No presets saved yet</span>
              <button
                className="effects-preset-empty-action"
                onClick={() => {
                  onSavePreset();
                  setIsOpen(false);
                }}
                type="button"
              >
                Save your first preset
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
